import { useDataChange } from 'Simple/Data.js'
import { normalizePath, useSetFlowTo } from 'Simple/Flow.js'
import {
  password as isValidPassword,
  sixDigits as isValidSixDigits,
} from 'Data/validate.js'
import {
  notifyError,
  notifyInvalid,
  useNotifications,
} from 'Logic/Notifications.js'
import { login } from 'Data/format.js'
import { setSkipMfaToken } from 'Data/Auth.js'
import { useMutation } from 'Data/Api.js'
import mutationMfa from './mutation-mfa.graphql.js'
import mutationResetPassword from './mutation-reset-password.graphql.js'

export default function useDataOnSubmit(props) {
  let onActionResetPassword = useDataOnActionResetPassword(props)
  let onActionMfa = useDataOnActionMfa(props)

  return async function onSubmit({ value, change, args }) {
    switch (args.type) {
      case 'reset_password': {
        return onActionResetPassword({ value, change, args })
      }
      case 'mfa': {
        return onActionMfa({ value, change, args })
      }
      default: {
      }
    }
  }
}

function useDataOnActionResetPassword(props) {
  let [, notify] = useNotifications()
  let [, executeMutation] = useMutation(mutationResetPassword)
  let setFlowTo = useSetFlowTo(props.viewPath)
  let changeAuth = useDataChange({
    viewPath: props.viewPath,
    context: 'auth',
  })

  return async function onSubmit({ value, change }) {
    if (isInvalid(value, notify)) return true

    let mutationResponse = await executeMutation(
      {
        email: value.email,
        code: value.code,
        password: value.password,
      },
      {
        fetchOptions: {
          headers: {
            'x-hasura-role': 'public',
          },
        },
      }
    )

    if (mutationResponse.data?.auth_reset_password.status === 'mfa_required') {
      change(next => {
        next.mfa = {
          ...mutationResponse.data.auth_reset_password.mfa,
          skip_next: false,
        }
      })
      setFlowTo(normalizePath(props.viewPath, 'TwoFactorAuth'))
    } else if (
      mutationResponse.error ||
      mutationResponse.data.auth_reset_password.status !== 'ok'
    ) {
      notify(notifyError('Something went wrong. Please contact your clinic.'))
    } else {
      changeAuth(login(mutationResponse.data.auth_reset_password))
      setFlowTo(normalizePath(props.viewPath, '../Check'))
    }
  }

  function isInvalid(value, notify) {
    let list = [!isValidPassword(value.password) && 'password'].filter(Boolean)

    if (list.length > 0) {
      notify(notifyInvalid(list))
      return true
    }
    return false
  }
}

function useDataOnActionMfa(props) {
  let [, notify] = useNotifications()
  let dataAuthChange = useDataChange({
    viewPath: props.viewPath,
    context: 'auth',
  })
  let [, executeMutation] = useMutation(mutationMfa)
  let setFlowTo = useSetFlowTo(props.viewPath)

  return async function onAction({ value }) {
    if (isInvalid(value, notify)) return true

    let mutationResponse = await executeMutation(
      {
        ticket: value.mfa.ticket,
        token: value.mfa.token,
        skip_next: value.mfa.skip_next,
      },
      {
        fetchOptions: {
          headers: {
            'x-hasura-role': 'public',
          },
        },
      }
    )

    if (
      mutationResponse.error ||
      mutationResponse.data.vaxiom_users_login_mfa.status !== 'ok'
    ) {
      return notify(
        notifyError('Please verify the two-factor authentication token')
      )
    }

    if (value.mfa.skip_next) {
      setSkipMfaToken(
        value.email,
        mutationResponse.data.vaxiom_users_login_mfa.skip_mfa_token
      )
    }

    dataAuthChange(login(mutationResponse.data.vaxiom_users_login_mfa))
    setFlowTo(normalizePath(props.viewPath, '../Check'))
  }

  function isInvalid(value, notify) {
    let list = [
      !isValidSixDigits(value.mfa.token) && 'two-factor auth token',
    ].filter(Boolean)

    if (list.length > 0) {
      notify(notifyInvalid(list))
      return true
    }
    return false
  }
}
